import React, {Fragment} from 'react';
import {EditableTag, FormField, Popup} from 'components';
import {capitalize, includes, over, partialRight, hoursInDay, minutesInHour, makeRules, isNumber, clamp, noop, padStart, useField, useBuffer, useFocusPopup} from 'helpers';
import css from './time-picker.module.scss';

export function TimePicker({ autoFocus, label, hasSuggestions = true, Wrapper = FormField, placeholder, ...props }) {
  const [ { value }, , { setValue } ] = useField(props);
  const [buffer, {focused}] = useBuffer({
    value,
    stringify,
    parse: partialRight(parse, props),
    onChange: ({ parsed }) => setValue(parsed),
  });

  const popup = hasSuggestions && useFocusPopup(TimeSuggestions, {
    ...props,
    ...buffer,
    setValue,
    deps: [buffer.value],
    className: 'right-to-left'
  });
  const hint = props.hasDayParts ? 'Enter time or pick a part of the day..' : 'Enter time..';
  return (
    <Wrapper {...props} value={buffer.value} label={focused ? hint : label} {...popup} placeholder={focused ? 'hh:mm am/pm' : placeholder} clear={() => setValue('')}>
      <input autoFocus={autoFocus} placeholder={Wrapper == FormField && focused ? 'hh:mm am/pm' : ''} {...buffer} className={css.input} />
    </Wrapper>
  );
}

export function TimeTagPicker(props) {
  return (
    <TimePicker Wrapper={EditableTag} {...props} />
  );
}

const rules = makeRules([
  /(?<dayParts>morning|afternoon|evening)/i,
  /(?<hours>\d\d?)\s*[^0-9apm]\s*(?<minutes>\d\d?)\s*(?<daytime>[ap]m?)?/i,
  /(?<hours>\d\d?)\s*(?<daytime>[ap]m?)?/i,
]);

function parse({ value, valueBeforeFocus }, { required }) {
  const match = value && rules.match(value);
  value = '';
  if (match && match.dayParts) {
    return { dayParts: capitalize(match.dayParts) };
  } else if (match) {
    let { hours, minutes = 0, daytime } = match;
    daytime = /p/i.test(daytime) ? 'PM' : 'AM';
    hours = clamp(parseInt(hours), 0, 23);
    minutes = clamp(parseInt(minutes), 0, 59);
    if (hours === 0) {
      daytime = 'AM';
    } else if (hours == 12 && minutes) {
      daytime = 'PM';
    } else if (hours > 12) {
      daytime = 'PM';
      hours -= 12;
    }
    if (isNumber(hours) || isNumber(minutes)) {
      value = `${hours}:${padStart(minutes, 2, 0)} ${daytime}`;
    }
  }
  if (required && !value) {
    value = valueBeforeFocus;
  }
  return value;
}

function stringify(value) {
  if (value && value.dayParts) {
    return value.dayParts;
  }
  return value;
}

function TimeSuggestions({ required, hasDayParts, popup, value, setValue }) {
  const match = rules.match(value) || {};
  const update = values => {
    const { hours, minutes, daytime } = {
      daytime: 'AM',
      hours: '12',
      minutes: '00',
      ...match,
      ...values,
    }
    setValue(`${hours}:${minutes} ${daytime}`, false);
  }
  return (
    <Fragment>
      <div className={css.columns}>
        <Popup.Menu className={css.menu} onClick={noop}>
          { hoursInDay.map(hours =>
            <Popup.Menu.Item
              key={hours}
              children={hours}
              aria-current={hours === '11 AM' ? 'time' : undefined}
              onClick={() => update(rules.match(hours))}
              pressed={hours == `${match.hours} ${match.daytime}`}
            />
          ) }
        </Popup.Menu>
        <Popup.Menu className={css.menu} onClick={noop}>
          { minutesInHour.map(minutes =>
            <Popup.Menu.Item key={minutes} pressed={minutes == match.minutes} onClick={() => update({ minutes })}>
              { minutes }
            </Popup.Menu.Item>
          ) }
        </Popup.Menu>
      </div>
      { hasDayParts &&
        <Popup.Menu>
          <Popup.Menu.Separator />
          <Popup.Menu.Item icon="morning" onClick={over(() => setValue('Morning'), popup.close)} pressed={includes(value, 'Morning')}>Morning</Popup.Menu.Item>
          <Popup.Menu.Item icon="afternoon" onClick={over(() => setValue('Afternoon'), popup.close)} pressed={includes(value, 'Afternoon')}>Afternoon</Popup.Menu.Item>
          <Popup.Menu.Item icon="evening" onClick={over(() => setValue('Evening'), popup.close)} pressed={includes(value, 'Evening')}>Evening</Popup.Menu.Item>
        </Popup.Menu>
      }
    </Fragment>
  );
}

